import React, { useState, useCallback, useEffect, useRef } from 'react';
import { BASE_URL, API_ENDPOINTS } from '../../config/apiconfig';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Button,
  Chip,
  Divider,
  Typography,
  InputBase,
  TextField,
  InputAdornment,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import api from '../../api/axiosInstance';
import appPreloader from '../../services/appPreloader';
import ConfirmModal from '../common/ConfirmModal';
import { toast } from 'react-toastify';
import {
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountCircleIcon,
  Add as AddIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Menu as MenuIcon,
  ArrowBack as ArrowBackIcon,
  Category as GoodsIcon,
  PropaneTank as CylinderIcon,
  People as CustomersIcon,
  Group as GroupIcon,
  LocalShipping as TruckIcon,
  Check as CheckIcon,
  Language as LanguageIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  ExpandMore as ExpandMoreIcon,
  Logout as LogoutIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  PhotoCamera as PhotoCameraIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { TokenManager } from '../../utils/tokenManager';
import { useColorMode } from '../../contexts/ColorModeContext';
import { SEARCH_CONFIG, searchHeaderResults, getHeaderSearchOptions } from '../../services/headerSearchService';

// ---- Design tokens (kept consistent with Sidebar.jsx) ----
// const COLORS = {
//   primary: '#4338CA',
//   primaryHover: '#3730A3',
//   primarySoft: '#EEF2FF',
//   primarySoftBorder: '#E0E7FF',
//   ink: '#0F172A',
//   inkSoft: '#475569',
//   muted: '#94A3B8',
//   border: '#E7EAF0',
//   surface: '#FFFFFF',
//   danger: '#DC2626',
// };

// Custom WhatsApp Icon from SVG path
const WhatsappIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" {...props}>
    <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
  </svg>
);

const Header = ({ sidebarOpen, isDesktop, onMobileMenuOpen }) => {

  // Must match Sidebar.jsx/MainLayout.jsx desktop drawer width.
  // Keeping this local prevents the header from drifting when the sidebar is compacted.
  const drawerWidth = 240;
  const navigate = useNavigate();
  const { toggleColorMode, mode } = useColorMode();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [quickAddAnchor, setQuickAddAnchor] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [searchIconAnchor, setSearchIconAnchor] = useState(null);
  const [selectedSearchType, setSelectedSearchType] = useState('Customers');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showResultsDropdown, setShowResultsDropdown] = useState(false);
  const [resultsAnchor, setResultsAnchor] = useState(null);
  const [activeResultIndex, setActiveResultIndex] = useState(-1);
  const [searchPage, setSearchPage] = useState(0);
  const [searchTotalPages, setSearchTotalPages] = useState(0);
  const [searchTotalElements, setSearchTotalElements] = useState(0);
  const searchRequestIdRef = useRef(0);
  const [whatsappCredits, setWhatsappCredits] = useState(null);
  const [businessName, setBusinessName] = useState('');
  const [loadingBusinessName, setLoadingBusinessName] = useState(true);
  const [loadingWhatsappCredits, setLoadingWhatsappCredits] = useState(true);
  const [logoutModal, setLogoutModal] = useState({ isOpen: false });
  const [logoData, setLogoData] = useState(null);
  const [logoModalOpen, setLogoModalOpen] = useState(false);
  const [logoEditMode, setLogoEditMode] = useState(false);
  const [logoDeleteConfirm, setLogoDeleteConfirm] = useState(false);
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const PROFILE_ID = 1;
  const LOCAL_LOGO_KEY = 'ctms_business_logo';

  const isDark = mode === 'dark';

  const COLORS = {
    primary: isDark ? '#818CF8' : '#4338CA',
    primaryHover: isDark ? '#6366F1' : '#3730A3',
    primarySoft: isDark ? '#1E1B4B' : '#EEF2FF',
    primarySoftBorder: isDark ? '#312E81' : '#E0E7FF',
    ink: isDark ? '#F8FAFC' : '#0F172A',
    inkSoft: isDark ? '#CBD5E1' : '#475569',
    muted: isDark ? '#64748B' : '#94A3B8',
    border: isDark ? '#334155' : '#E7EAF0',
    surface: isDark ? '#0F172A' : '#FFFFFF',
    surfaceSoft: isDark ? '#1E293B' : '#F8FAFC',
    hoverSurface: isDark ? '#273449' : '#F1F5F9',
    controlBorder: isDark ? '#475569' : '#E2E8F0',
    borderSoft: isDark ? '#3B475A' : '#EDF0F5',
    resultActive: isDark ? '#1E293B' : '#F7F8FF',
    resultIcon: isDark ? '#172554' : '#F1F3FF',
    resultIconActive: isDark ? '#312E81' : '#E9E7FF',
    footer: isDark ? '#111C2E' : '#FAFBFD',
    scrollThumb: isDark ? '#475569' : '#CBD5E1',
    scrollThumbHover: isDark ? '#64748B' : '#94A3B8',
    successText: isDark ? '#86EFAC' : '#166534',
    successBg: isDark ? '#052E16' : '#F0FDF4',
    successBorder: isDark ? '#166534' : '#DCFCE7',
    dangerBg: isDark ? '#450A0A' : '#FEE2E2',
    dangerBorder: isDark ? '#7F1D1D' : '#FCA5A5',
    dangerHover: isDark ? '#7F1D1D' : '#B91C1C',
    highlightBg: isDark ? '#713F12' : '#FEF3C7',
    highlightText: isDark ? '#FDE68A' : '#92400E',
    menuShadow: isDark ? '0 10px 24px -6px rgba(0, 0, 0, 0.45)' : '0 10px 24px -6px rgba(15, 23, 42, 0.12)',
    resultsShadow: isDark ? '0 28px 70px -28px rgba(0, 0, 0, 0.65), 0 8px 24px rgba(0, 0, 0, 0.35)' : '0 28px 70px -28px rgba(15, 23, 42, 0.38), 0 8px 24px rgba(15, 23, 42, 0.08)',
    resultActiveShadow: isDark ? '0 5px 16px rgba(99, 102, 241, 0.18)' : '0 5px 16px rgba(67, 56, 202, 0.08)',
    subtleShadow: isDark ? '0 1px 2px rgba(0, 0, 0, 0.25)' : '0 1px 2px rgba(15, 23, 42, 0.02)',
    danger: '#EF4444',
  };

  // Load logo from localStorage on component mount
  useEffect(() => {
    try {
      const savedLogo = localStorage.getItem(LOCAL_LOGO_KEY);
      if (savedLogo) {
        setLogoData(savedLogo);
      }
    } catch (error) {
      console.error('Failed to load local logo:', error);
    }
  }, []);

  // Helper to normalize logo data URL for different image formats
  const normalizeLogo = (logo) => {
    if (!logo) return null;
    if (logo.startsWith('data:')) return logo;
    return `data:image/png;base64,${logo}`;
  };

  const searchOptions = getHeaderSearchOptions();

  // Logo file change handler
  const handleLogoFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const allowedTypes = [
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/webp',
      'image/svg+xml',
    ];

    if (!allowedTypes.includes(file.type)) {
      alert(
        'Please select PNG, JPG, JPEG, WEBP or SVG image.'
      );
      event.target.value = '';
      return;
    }

    // 5 MB limit
    if (file.size > 5 * 1024 * 1024) {
      alert('Logo size must be less than 5 MB.');
      event.target.value = '';
      return;
    }

    setSelectedLogoFile(file);

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      setLogoPreview(result);
    };
    reader.onerror = () => {
      alert('Unable to read the selected logo.');
    };
    reader.readAsDataURL(file);
  };

  // Save logo to localStorage and API
  const handleSaveLogo = async () => {
    if (!logoPreview) {
      return;
    }

    const token = localStorage.getItem('token');

    try {
      // First save to localStorage for immediate UI update
      localStorage.setItem(
        LOCAL_LOGO_KEY,
        logoPreview
      );
      setLogoData(logoPreview);

      // Show success toast immediately
      toast.success('Logo updated successfully!');

      // Close modal immediately
      setSelectedLogoFile(null);
      setLogoPreview(null);
      setLogoEditMode(false);
      setLogoModalOpen(false);

      // Notify other components/tabs immediately
      window.dispatchEvent(
        new CustomEvent('businessLogoUpdated', {
          detail: logoPreview,
        })
      );

      // Then update via API in the background - fetch current settings first
      const getRes = await fetch(
        `${BASE_URL}${API_ENDPOINTS.SETTINGS.GET}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!getRes.ok) {
        throw new Error('Failed to fetch current settings');
      }

      const currentSettings = await getRes.json();

      console.log('handleSaveLogo - Current settings fetched, has logoBase64:', !!currentSettings.logoBase64);

      // Prepare full payload with new logo - send entire settings object with updated logo
      const payload = {
        ...currentSettings,
        logoBase64: logoPreview,
      };

      const res = await fetch(
        `${BASE_URL}${API_ENDPOINTS.SETTINGS.UPDATE}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        throw new Error('Failed to update logo on server');
      }
    } catch (error) {
      console.error(
        'Failed to save logo:',
        error
      );
      // Revert localStorage if API fails
      localStorage.removeItem(LOCAL_LOGO_KEY);
      setLogoData(null);

      if (
        error.name === 'QuotaExceededError'
      ) {
        toast.error(
          'Logo could not be saved. Please use a smaller image.'
        );
      } else {
        toast.error('Failed to update logo on server. Please try again.');
      }
    }
  };

  // Delete logo from localStorage and API
  const handleDeleteLogo = async () => {
    const token = localStorage.getItem('token');
    console.log('handleDeleteLogo - Starting delete process');
    console.log('handleDeleteLogo - Token exists:', !!token);

    try {
      // First fetch current settings to preserve other fields
      console.log('handleDeleteLogo - Fetching current settings');
      const getRes = await fetch(
        `${BASE_URL}${API_ENDPOINTS.SETTINGS.GET}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log('handleDeleteLogo - GET response status:', getRes.status);

      if (!getRes.ok) {
        throw new Error('Failed to fetch current settings');
      }

      const currentSettings = await getRes.json();

      console.log('handleDeleteLogo - Current settings fetched, has logoBase64:', !!currentSettings.logoBase64);

      // Then update via API to remove logo while preserving other settings
      // Send the entire currentSettings object with logoBase64 set to null
      const payload = {
        ...currentSettings,
        logoBase64: null,
      };

      console.log('handleDeleteLogo - Sending PUT request with logoBase64: null');
      const res = await fetch(
        `${BASE_URL}${API_ENDPOINTS.SETTINGS.UPDATE}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      console.log('handleDeleteLogo - PUT response status:', res.status);

      if (!res.ok) {
        throw new Error('Failed to delete logo on server');
      }

      console.log('handleDeleteLogo - API delete successful, removing from localStorage');
      // Then remove from localStorage for UI update
      localStorage.removeItem(
        LOCAL_LOGO_KEY
      );
      console.log('handleDeleteLogo - localStorage removed, updating state');
      setLogoData(null);
      setLogoPreview(null);
      setSelectedLogoFile(null);
      setLogoDeleteConfirm(false);
      setLogoEditMode(false);

      window.dispatchEvent(
        new CustomEvent('businessLogoUpdated', {
          detail: null,
        })
      );
    } catch (error) {
      console.error(
        'Failed to delete logo:',
        error
      );
      alert('Failed to delete logo. Please try again.');
    }
  };

  // Download logo
  const handleDownloadLogo = () => {
    const logo = logoData;
    if (!logo) {
      alert('No logo available to download.');
      return;
    }

    try {
      const link = document.createElement('a');
      link.href = logo;
      link.download = 'business-logo.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error(
        'Failed to download logo:',
        error
      );
    }
  };

  // Handle logo click to open popup
  const handleLogoClick = (event) => {
    event?.stopPropagation?.();
    setAnchorEl(null);
    setLogoEditMode(false);
    setLogoDeleteConfirm(false);
    setLogoPreview(null);
    setSelectedLogoFile(null);
    setLogoModalOpen(true);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleQuickAddMenuOpen = (event) => {
    setQuickAddAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setNotificationAnchor(null);
    setQuickAddAnchor(null);
  };

  // Debounced header search using the shared search service.
  // A request id prevents slower previous searches from overwriting newer results.
  const performSearch = useCallback(async (query, type, page = 0) => {
    const trimmedQuery = query?.trim() || '';
    const requestId = ++searchRequestIdRef.current;

    if (!trimmedQuery || trimmedQuery.length < 2 || !SEARCH_CONFIG[type]) {
      setSearchResults([]);
      setShowResultsDropdown(false);
      setSearchLoading(false);
      setActiveResultIndex(-1);
      setSearchPage(0);
      setSearchTotalPages(0);
      setSearchTotalElements(0);
      return;
    }

    setSearchLoading(true);
    setShowResultsDropdown(true);

    try {
      const response = await searchHeaderResults(type, trimmedQuery, page, 10);

      // Ignore stale responses when the user keeps typing or changes search type.
      if (requestId !== searchRequestIdRef.current) return;

      const results = Array.isArray(response?.results) ? response.results : [];
      setSearchResults(results);
      setSearchTotalPages(response?.totalPages || 0);
      setSearchTotalElements(response?.totalElements || results.length);
      setActiveResultIndex(results.length ? 0 : -1);
    } catch (error) {
      if (requestId !== searchRequestIdRef.current) return;
      console.error('Header search error:', error);
      setSearchResults([]);
      setSearchTotalPages(0);
      setSearchTotalElements(0);
      setActiveResultIndex(-1);
    } finally {
      if (requestId === searchRequestIdRef.current) {
        setSearchLoading(false);
      }
    }
  }, []);

  // Reset pagination when search type changes
  useEffect(() => {
    setSearchPage(0);
    setSearchTotalPages(0);
    setSearchTotalElements(0);
  }, [selectedSearchType]);

  // Debounce effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchPage(0);
      performSearch(searchQuery, selectedSearchType, 0);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, selectedSearchType, performSearch]);

  const getSearchResultIcon = (type) => {
    const iconProps = { fontSize: 'small' };
    switch (type) {
      case 'Customer':
      case 'Customers':
      case 'Customer Passbook':
      case 'User':
      case 'Users':
        return <CustomersIcon {...iconProps} />;
      case 'Vendor':
      case 'Vendors':
      case 'Vendor Passbook':
        return <GroupIcon {...iconProps} />;
      case 'Goods':
      case 'Items & Goods':
        return <GoodsIcon {...iconProps} />;
      case 'Challan':
      case 'Challans':
        return <TruckIcon {...iconProps} />;
      case 'Cylinder':
      case 'Cylinders':
        return <CylinderIcon {...iconProps} />;
      case 'Billing':
      case 'Billing Statements':
        return <DownloadIcon {...iconProps} />;
      default:
        return <SearchIcon {...iconProps} />;
    }
  };

  const getSearchListRoute = (type) => {
    switch (type) {
      case 'Customers':
      case 'Customer Passbook':
        return '/dashboard/customers';
      case 'Vendors':
      case 'Vendor Passbook':
        return type === 'Vendor Passbook' ? '/dashboard/purchases/vendor-passbook' : '/dashboard/vendors/view';
      case 'Billing Statements':
        return '/dashboard/billing-statements';
      case 'Items & Goods':
        return '/dashboard/inventory/goods';
      case 'Challans':
        return '/dashboard/challans/view';
      case 'Cylinders':
        return '/dashboard/inventory/cylinders';
      case 'Users':
        return '/dashboard/users';
      default:
        return '/dashboard';
    }
  };

  const highlightSearchText = (value) => {
    if (value === null || value === undefined) return null;
    const text = String(value);
    const query = searchQuery.trim();
    if (!query) return text;

    const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\\]\\]/g, '\\\\$&')})`, 'ig'));
    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <Box key={`${part}-${index}`} component="span" sx={{ backgroundColor: COLORS.highlightBg, color: COLORS.highlightText, px: 0.2, borderRadius: '3px', fontWeight: 800 }}>
          {part}
        </Box>
      ) : part
    );
  };

  // Fetch business name and logo from profile
  useEffect(() => {
    const fetchBusinessName = async () => {
      // Check if data is already preloaded
      const preloadedProfile = appPreloader.getCachedData('profile');
      console.log('Header - Preloaded profile data:', preloadedProfile);

      if (preloadedProfile) {
        // Handle different possible data structures
        const name = preloadedProfile.name || preloadedProfile.businessName || preloadedProfile.companyName;
        console.log('Header - Extracted business name from preload:', name);

        // Check for logo in preloaded profile data
        if (preloadedProfile.logoBase64 && !localStorage.getItem(LOCAL_LOGO_KEY)) {
          setLogoData(normalizeLogo(preloadedProfile.logoBase64));
          console.log('Header - Logo found in preloaded profile');
        }

        if (name) {
          setBusinessName(name);
          setLoadingBusinessName(false);
          return;
        }
      }

      setLoadingBusinessName(true);
      try {
        const API_URL = `${BASE_URL}${API_ENDPOINTS.PROFILE.GET}`;
        const res = await api.get(`${API_URL}/getById/${PROFILE_ID}`);
        console.log('Header - Profile API response:', res.data);

        // Handle different response structures
        const name = res.data?.name || res.data?.businessName || res.data?.companyName;
        console.log('Header - Extracted business name from API:', name);

        // Check for logo in profile API response
        if (res.data?.logoBase64 && !localStorage.getItem(LOCAL_LOGO_KEY)) {
          setLogoData(normalizeLogo(res.data.logoBase64));
          console.log('Header - Logo found in profile API response');
        }

        if (name) {
          setBusinessName(name);
          // Cache the data
          appPreloader.cache.set('profile', res.data);
          appPreloader.saveToLocalStorage();
        }
      } catch (error) {
        console.error('Error fetching business name:', error);
      } finally {
        setLoadingBusinessName(false);
      }
    };
    fetchBusinessName();

    const handleBusinessProfileUpdated = (event) => {
      const profile = event.detail || {};
      const name = profile.name || profile.businessName || profile.companyName;
      if (name) {
        setBusinessName(name);
        setLoadingBusinessName(false);
      }
      if (profile.logoBase64 || profile.logo) {
        setLogoData(normalizeLogo(profile.logoBase64 || profile.logo));
      }
      appPreloader.cache.set("profile", profile);
      appPreloader.saveToLocalStorage();
    };

    window.addEventListener("businessProfileUpdated", handleBusinessProfileUpdated);
    return () => window.removeEventListener("businessProfileUpdated", handleBusinessProfileUpdated);
  }, []);

  // Fetch logo from settings API (primary source)
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        console.log('Header - Fetching logo from settings API');
        const response = await fetch(`${BASE_URL}${API_ENDPOINTS.SETTINGS.GET}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        console.log('Header - Logo fetch response status:', response.status);

        if (response.ok) {
          const data = await response.json();
          console.log('Header - Settings API response data keys:', Object.keys(data));
          console.log('Header - logoBase64 present:', !!data.logoBase64);

          const localLogo = localStorage.getItem(LOCAL_LOGO_KEY);
          console.log('Header - localStorage logo exists:', !!localLogo);

          if (localLogo) {
            console.log('Header - Using localStorage logo');
            setLogoData(localLogo);
          } else if (data.logoBase64) {
            console.log('Header - Using API logo');
            setLogoData(normalizeLogo(data.logoBase64));
            console.log('Header - Setting logo data from settings API');
          } else {
            console.log('Header - No logoBase64 found in settings response, setting logoData to null');
            setLogoData(null);
          }
        } else {
          console.error('Header - Failed to fetch settings:', response.status);
        }
      } catch (error) {
        console.error('Header - Error fetching logo:', error);
      }
    };
    fetchLogo();
  }, []);

  // Handle result click with navigation logic.
  const handleResultClick = useCallback(async (result) => {
    setShowResultsDropdown(false);
    setSearchQuery('');
    setActiveResultIndex(-1);

    const id = result?.id || result?.original?.id;
    const quickSearch = result?.title || result?.subtitle || result?.meta || '';

    switch (selectedSearchType) {
      case 'Customers':
        if (!id) {
          navigate('/dashboard/customers', { state: { quickSearch } });
          return;
        }
        navigate('/dashboard/customers', { state: { quickSearch: result?.title || quickSearch } });
        break;
      case 'Customer Passbook':
        navigate('/dashboard/customers/passbook', { state: { customerId: id, customerName: result?.title } });
        break;
      case 'Vendors':
        navigate('/dashboard/vendors/view', { state: { selectedVendorId: id, quickSearch } });
        break;
      case 'Vendor Passbook':
        navigate('/dashboard/purchases/vendor-passbook', { state: { vendorId: id, vendorName: result?.title } });
        break;
      case 'Billing Statements':
        navigate('/dashboard/billing-statements', { state: { selectedCustomerId: id, customerName: result?.title } });
        break;
      case 'Items & Goods':
        navigate('/dashboard/inventory/goods', { state: { selectedGoodsId: id, quickSearch } });
        break;
      case 'Challans':
        navigate('/dashboard/challans/view', { state: { selectedChallanId: id, quickSearch } });
        break;
      case 'Cylinders':
        navigate('/dashboard/inventory/cylinders', { state: { selectedCylinderId: id, quickSearch } });
        break;
      case 'Users':
        navigate('/dashboard/users', { state: { selectedUserId: id, quickSearch } });
        break;
      default:
        navigate('/dashboard', { state: quickSearch ? { quickSearch } : undefined });
    }
  }, [navigate, selectedSearchType]);

  // Keyboard navigation inside search results.
  useEffect(() => {
    const handleSearchKeyDown = (event) => {
      if (!showResultsDropdown || !searchResults.length) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveResultIndex((current) => (current + 1) % searchResults.length);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveResultIndex((current) => (current - 1 + searchResults.length) % searchResults.length);
      } else if (event.key === 'Enter' && activeResultIndex >= 0) {
        event.preventDefault();
        handleResultClick(searchResults[activeResultIndex]);
      } else if (event.key === 'Escape') {
        setShowResultsDropdown(false);
        setActiveResultIndex(-1);
      }
    };

    window.addEventListener('keydown', handleSearchKeyDown);
    return () => window.removeEventListener('keydown', handleSearchKeyDown);
  }, [activeResultIndex, searchResults, showResultsDropdown, handleResultClick]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.altKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        const searchInput = document.querySelector('input[placeholder*="Search"]');
        if (searchInput) {
          searchInput.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Fetch WhatsApp Credits
  useEffect(() => {
    const fetchWhatsappCredits = async () => {
      // Check if data is already preloaded
      const preloadedCredits = appPreloader.getCachedData('whatsappCredits');
      console.log('Header - Preloaded WhatsApp credits check:', preloadedCredits);

      if (preloadedCredits !== null) {
        console.log('Header - Using preloaded WhatsApp credits:', preloadedCredits);
        setWhatsappCredits(preloadedCredits);
        setLoadingWhatsappCredits(false);
        return;
      }

      console.log('Header - No preloaded data, fetching from API');
      setLoadingWhatsappCredits(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const response = await fetch(`${BASE_URL}/client-config/current/whatsapp-credits`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          console.log('Header - Fetched WhatsApp credits from API:', data);
          console.log('Header - WhatsApp credits data type:', typeof data);
          console.log('Header - WhatsApp credits data keys:', data ? Object.keys(data) : 'null/undefined');

          // Handle different response structures
          // The API might return { credits: 100 } or just 100 directly
          const creditsValue = data?.credits || data?.value || data?.count || data;
          console.log('Header - Extracted credits value:', creditsValue);

          setWhatsappCredits(creditsValue);
          // Cache the data for future use
          appPreloader.cache.set('whatsappCredits', creditsValue);
        } else {
          console.error('Failed to fetch WhatsApp credits:', response.status);
        }
      } catch (error) {
        console.error('Error fetching WhatsApp credits:', error);
      } finally {
        setLoadingWhatsappCredits(false);
      }
    };
    fetchWhatsappCredits();

    // Listen for WhatsApp credits update events from dashboard refresh
    const handleCreditsUpdate = (event) => {
      console.log('Header - Received WhatsApp credits update event:', event.detail);
      setWhatsappCredits(event.detail);
      appPreloader.cache.set('whatsappCredits', event.detail);
    };

    window.addEventListener('whatsappCreditsUpdated', handleCreditsUpdate);

    return () => {
      window.removeEventListener('whatsappCreditsUpdated', handleCreditsUpdate);
    };
  }, []);

  // Cross-tab synchronization for logo updates
  useEffect(() => {
    const handleLogoUpdate = (event) => {
      setLogoData(event.detail || null);
    };

    const handleStorage = (event) => {
      if (event.key === LOCAL_LOGO_KEY) {
        setLogoData(event.newValue || null);
      }
    };

    window.addEventListener(
      'businessLogoUpdated',
      handleLogoUpdate
    );

    window.addEventListener(
      'storage',
      handleStorage
    );

    return () => {
      window.removeEventListener(
        'businessLogoUpdated',
        handleLogoUpdate
      );

      window.removeEventListener(
        'storage',
        handleStorage
      );
    };
  }, []);

  const handleLogout = () => {
    handleMenuClose();
    setLogoutModal({ isOpen: true });
  };

  const confirmLogout = () => {
    TokenManager.clearToken();
    appPreloader.clearCache();
    window.location.href = '/login';
  };

  const cancelLogout = () => {
    setLogoutModal({ isOpen: false });
  };

  const quickAddCategories = [
    {
      title: 'CHALLANS',
      items: [
        { label: 'Delivered Challan', path: '/dashboard/challans/delivered', icon: <TruckIcon /> },
        { label: 'Received Challan', path: '/dashboard/challans/received', icon: <TruckIcon /> },
        { label: 'Combined Challan', path: '/dashboard/challans/combined', icon: <TruckIcon /> },
      ],
    },
    {
      title: 'GENERAL',
      items: [
        { label: 'Add User', path: '/dashboard/users', icon: <AccountCircleIcon /> },
        { label: 'Customer', path: '/dashboard/customers/create', icon: <CustomersIcon /> },
        // { label: 'Vendor', path: '/dashboard/purchases/vendors/create', icon: <BusinessIcon /> },
      ],
    },
    {
      title: 'INVENTORY',
      items: [
        { label: 'Goods', path: '/dashboard/inventory/goods', icon: <GoodsIcon /> },
        { label: 'Cylinder', path: '/dashboard/inventory/cylinders', icon: <CylinderIcon /> },
        // { label: 'Adjustment', path: '/dashboard/inventory/adjustment', icon: <AssessmentIcon /> },
      ],
    },
    // {
    //   title: 'PURCHASES',
    //   items: [
    //     { label: 'Purchase Order', path: '/dashboard/purchases/orders', icon: <PurchasesIcon /> },
    //     { label: 'Vendor Payment', path: '/dashboard/purchases/payments', icon: <BankingIcon /> },
    //   ],
    // },
    // {
    //   title: 'BANKING',
    //   items: [
    //     { label: 'Bank Transfer', path: '/dashboard/banking/transactions', icon: <BankingIcon /> },
    //     { label: 'Cash Entry', path: '/dashboard/banking/cash', icon: <BankingIcon /> },
    //   ],
    // },
  ];

  const notificationItems = [
    { title: 'New Invoice Created', description: 'Invoice #INV-001 created for Customer A', time: '2 min ago' },
    { title: 'Payment Received', description: '50,000 received from Customer B', time: '1 hour ago' },
    { title: 'Low Stock Alert', description: 'Cylinder Type A is running low (5 units)', time: '3 hours ago' },
    { title: 'Overdue Reminder', description: '3 invoices are overdue', time: '1 day ago' },
  ];

  const renderSearchTypeMenu = () => (

    <Menu
      anchorEl={searchIconAnchor}
      open={Boolean(searchIconAnchor)}
      onClose={() => setSearchIconAnchor(null)}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      marginThreshold={8}
      slotProps={{
        paper: {
          sx: {
            width: { xs: 'min(280px, calc(100vw - 16px))', sm: 'min(320px, calc(100vw - 16px))' },
            maxWidth: 'calc(100vw - 16px)',
            mt: 0.5,
            borderRadius: '10px',
            overflow: 'hidden',
            border: `1px solid ${COLORS.border}`,
            boxShadow: COLORS.menuShadow,
            boxSizing: 'border-box',
          },
        },
      }}
    >
      {searchOptions.map((item) => (
        <MenuItem
          key={item}
          selected={item === selectedSearchType}
          onClick={() => {
            setSelectedSearchType(item);
            setSearchIconAnchor(null);
            setActiveResultIndex(-1);
          }}
          sx={{
            height: 40,
            fontSize: 14,
            backgroundColor: item === selectedSearchType ? COLORS.primary : "transparent",
            color: item === selectedSearchType ? "#fff" : COLORS.ink,
            "&:hover": {
              backgroundColor: item === selectedSearchType ? COLORS.primaryHover : COLORS.hoverSurface,
            },
            "&.Mui-selected": {
              backgroundColor: COLORS.primary + " !important",
              color: "#fff !important",
            },
            "&.Mui-selected:hover": {
              backgroundColor: COLORS.primaryHover + " !important",
              color: "#fff !important",
            },
            "&.Mui-selected .MuiTypography-root": {
              color: "#fff !important",
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <span style={{ color: item === selectedSearchType ? "#fff" : "inherit", fontWeight: 500 }}>
              {item}
            </span>
          </Box>
          {item === selectedSearchType && (
            <CheckIcon sx={{ ml: "auto", fontSize: 18, color: "#fff" }} />
          )}
        </MenuItem>
      ))}
      <Divider />
      <MenuItem
        sx={{
          height: 40,
          fontSize: 14,
          "&:hover": {
            backgroundColor: COLORS.surfaceSoft,
          },
        }}
      >
        <SearchIcon sx={{ mr: 1, color: COLORS.primary, fontSize: 18 }} />
        Advanced Search
        <Box sx={{ ml: "auto", color: COLORS.muted, fontSize: 12 }}>Alt + /</Box>
      </MenuItem>
      <MenuItem
        sx={{
          height: 40,
          fontSize: 14,
          "&:hover": {
            backgroundColor: COLORS.surfaceSoft,
          },
        }}
      >
        <LanguageIcon sx={{ mr: 1, color: COLORS.primary, fontSize: 18 }} />
        Search across Zerlak
        <Box sx={{ ml: "auto", color: COLORS.muted, fontSize: 12 }}>Ctrl + /</Box>
      </MenuItem>
    </Menu>

  );

  const renderSearchResultsMenu = () => (

    <Menu
      anchorEl={resultsAnchor}
      open={showResultsDropdown}
      onClose={() => {
        setShowResultsDropdown(false);
        setActiveResultIndex(-1);
        setResultsAnchor(null);
      }}
      disableAutoFocusItem
      disablePortal={true}
      marginThreshold={0}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left'
      }}
      slotProps={{
        paper: {
          className: 'header-search-results-paper',
          sx: {
            // The search Menu is rendered from the header input, so MUI normally
            // uses the input's left edge as the Paper's horizontal position.
            // Keep MUI responsible for the vertical anchor, but explicitly center
            // the Paper inside the same main-content area used by MainLayout.
            position: 'fixed !important',
            // Keep the results panel directly below the fixed 64px header.
            // The previous version inherited MUI's anchor Y-position, which
            // created the large vertical gap visible in the dashboard.
            top: '72px !important',
            // Center relative to the actual main layout and the same drawer width.
            // On tablet/mobile the sidebar is not part of the layout, even though
            // MUI's `sm` breakpoint is active. Do not apply the 260px desktop
            // sidebar offset unless the layout is actually desktop.
            // Use the CENTER POINT of the actual content area.
            // `transform: translateX(-50%)` then centers the paper around that
            // point. The previous desktop formula calculated the paper's left
            // edge and then applied translateX(-50%), which shifted it toward
            // the sidebar.
            left: isDesktop
              ? `calc(${drawerWidth}px + ((100vw - ${drawerWidth}px) / 2)) !important`
              : '50% !important',
            transform: 'translateX(-50%) !important',
            width: {
              xs: 'calc(100vw - 16px)',
              sm: 'min(620px, calc(100vw - 24px))',
              md: 'min(620px, calc(100vw - 40px))',
              xl: '620px',
            },
            minWidth: 0,
            maxWidth: {
              xs: 'calc(100vw - 16px)',
              sm: 'calc(100vw - 24px)',
              md: '620px',
            },
            boxSizing: 'border-box',
            mt: 0,
            borderRadius: { xs: '14px', sm: '18px' },
            overflow: 'hidden',
            border: `1px solid ${COLORS.border}`,
            boxShadow: COLORS.resultsShadow,
            height: {
              xs: 'min(640px, calc(100dvh - 82px))',
              sm: 'min(650px, calc(100dvh - 96px))',
              md: 'min(650px, calc(100dvh - 96px))',
            },
            maxHeight: {
              xs: 'calc(100dvh - 82px)',
              sm: 'calc(100dvh - 96px)',
              md: 'calc(100dvh - 96px)',
            },
            // bgcolor: COLORS.surface,
            bgcolor: COLORS.surface,
            color: COLORS.ink,
            display: 'flex',
            flexDirection: 'column',
            '& .MuiList-root': {
              width: '100%',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              flex: '1 1 auto',
              minHeight: 0,
              height: '100%',
              maxHeight: 'none',
              overflow: 'hidden',
              padding: 0,
            },
          },
        },
        list: {
          sx: {
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 auto',
            minHeight: 0,
            height: '100%',
            maxHeight: 'none',
            overflow: 'hidden',
            '&::-webkit-scrollbar': { width: 7 },
            '&::-webkit-scrollbar-track': { backgroundColor: COLORS.surfaceSoft },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: COLORS.scrollThumb,
              borderRadius: 99,
              border: `2px solid ${COLORS.surfaceSoft}`,
            },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: COLORS.scrollThumbHover },
          },
        },
      }}
    >
      {/* Search header */}
      <Box
        className="header-search-results-header"
        sx={{
          flex: '0 0 auto',
          minHeight: 0,
          px: { xs: 1.5, sm: 2.25 },
          py: { xs: 1.35, sm: 1.75 },
          borderBottom: `1px solid ${COLORS.border}`,
          background: `linear-gradient(135deg, ${COLORS.surfaceSoft} 0%, ${COLORS.surface} 65%, ${COLORS.primarySoft} 100%)`,
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: { xs: 1, sm: 2 },
        }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.45 }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '9px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: COLORS.primarySoft,
                  color: COLORS.primary,
                  border: `1px solid ${COLORS.primarySoftBorder}`,
                  flexShrink: 0,
                }}
              >
                <SearchIcon sx={{ fontSize: 17 }} />
              </Box>
              <Typography sx={{ fontSize: 15, fontWeight: 850, color: COLORS.ink, lineHeight: 1.2 }}>
                Search results
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: { xs: 11, sm: 12 },
                color: COLORS.inkSoft,
                ml: { xs: 0, sm: 5 },
                lineHeight: 1.35,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {searchLoading
                ? `Searching ${selectedSearchType}...`
                : `${searchResults.length} match${searchResults.length === 1 ? '' : 'es'} found for “${searchQuery.trim()}”`}
            </Typography>
          </Box>

          <Chip
            size="small"
            label={selectedSearchType}
            icon={getSearchResultIcon(SEARCH_CONFIG[selectedSearchType]?.type || selectedSearchType)}
            sx={{
              height: { xs: 27, sm: 30 },
              borderRadius: { xs: '8px', sm: '9px' },
              bgcolor: COLORS.primarySoft,
              color: COLORS.primary,
              border: `1px solid ${COLORS.primarySoftBorder}`,
              fontSize: 11,
              fontWeight: 800,
              flexShrink: 0,
              '& .MuiChip-icon': { color: COLORS.primary, fontSize: 16 },
              '& .MuiChip-label': { px: 1.1 },
            }}
          />
        </Box>
      </Box>

      {/* Search controls inside results dropdown */}
      <Box
        sx={{
          flex: '0 0 auto',
          px: { xs: 1.25, sm: 1.75 },
          py: { xs: 0.9, sm: 1 },
          borderBottom: `1px solid ${COLORS.border}`,
          bgcolor: COLORS.surface,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            width: '100%',
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              minWidth: 0,
              height: { xs: 38, sm: 40 },
              px: 1.1,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '9px',
              bgcolor: COLORS.surfaceSoft,
              transition: 'all .2s ease',
              '&:focus-within': {
                bgcolor: COLORS.surface,
                borderColor: COLORS.primary,
                boxShadow: `0 0 0 3px ${COLORS.primarySoft}`,
              },
            }}
          >
            <SearchIcon sx={{ color: COLORS.primary, fontSize: 18, mr: 0.75, flexShrink: 0 }} />
            <InputBase
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={`Search ${selectedSearchType}...`}
              inputProps={{ 'aria-label': `Search ${selectedSearchType}` }}
              sx={{
                minWidth: 0,
                fontSize: { xs: 12.5, sm: 13.5 },
                color: COLORS.ink,
                '& input': {
                  minWidth: 0,
                  py: 0.25,
                },
              }}
            />
          </Box>

          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSearchIconAnchor(e.currentTarget);
            }}
            endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 17 }} />}
            startIcon={getSearchResultIcon(SEARCH_CONFIG[selectedSearchType]?.type || selectedSearchType)}
            sx={{
              flexShrink: 0,
              height: { xs: 38, sm: 40 },
              minWidth: { xs: 110, sm: 130 },
              maxWidth: { xs: 160, sm: 180 },
              px: { xs: 0.9, sm: 1.1 },
              borderRadius: '9px',
              border: `1px solid ${COLORS.primarySoftBorder}`,
              bgcolor: COLORS.primarySoft,
              color: COLORS.primary,
              textTransform: 'none',
              fontSize: { xs: 10.5, sm: 11.5 },
              fontWeight: 800,
              whiteSpace: 'nowrap',
              overflow: 'visible',
              '& .MuiButton-startIcon': {
                marginLeft: 0,
                marginRight: 0.45,
              },
              '& .MuiButton-endIcon': {
                marginLeft: 0.35,
                marginRight: 0,
              },
              '&:hover': {
                bgcolor: COLORS.primarySoftBorder,
                borderColor: COLORS.primarySoftBorder,
              },
              '& .MuiButton-startIcon svg': {
                fontSize: 16,
              },
            }}
          >
            <Box
              component="span"
              sx={{
                overflow: 'visible',
                textOverflow: 'clip',
                whiteSpace: 'nowrap',
              }}
            >
              {selectedSearchType}
            </Box>
          </Button>
        </Box>
      </Box>

      {searchLoading ? (
        <Box
          className="header-search-results-scroll"
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            p: { xs: 1, sm: 1.5 },
            WebkitOverflowScrolling: 'touch',
            '&::-webkit-scrollbar': { width: 7 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: COLORS.scrollThumb, borderRadius: 99 },
            '&::-webkit-scrollbar-track': { backgroundColor: COLORS.surfaceSoft },
          }}
        >
          {[1, 2, 3].map((item) => (
            <Box
              key={item}
              sx={{
                display: 'flex',
                gap: { xs: 1, sm: 1.5 },
                p: { xs: 1, sm: 1.5 },
                mb: 0.75,
                borderRadius: '13px',
                border: `1px solid ${COLORS.border}`,
              }}
            >
              <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: '12px', flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width="45%" height={22} />
                <Skeleton width="72%" height={17} />
                <Skeleton width="55%" height={15} />
              </Box>
            </Box>
          ))}
        </Box>
      ) : searchResults.length === 0 ? (
        <Box
          className="header-search-results-scroll"
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            px: { xs: 2, sm: 3 },
            py: { xs: 3.5, sm: 5 },
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 54,
              height: 54,
              mx: 'auto',
              mb: 1.5,
              borderRadius: '15px',
              bgcolor: COLORS.surfaceSoft,
              border: `1px solid ${COLORS.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: COLORS.muted,
            }}
          >
            <SearchIcon sx={{ fontSize: 24 }} />
          </Box>
          <Typography sx={{ color: COLORS.ink, fontSize: 14, fontWeight: 800 }}>
            No results found
          </Typography>
          <Typography sx={{ color: COLORS.muted, fontSize: 12, mt: 0.6 }}>
            Try another keyword or switch the search type.
          </Typography>
        </Box>
      ) : (
        <Box
          className="header-search-results-scroll"
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            p: { xs: 0.65, sm: 1 },
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            '&::-webkit-scrollbar': { width: 7 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: COLORS.scrollThumb, borderRadius: 99 },
            '&::-webkit-scrollbar-track': { backgroundColor: COLORS.surfaceSoft },
          }}
        >
          {searchResults.map((result, index) => {
            const resultType = result?.type || selectedSearchType;
            const original = result?.original || {};
            const isActive = index === activeResultIndex;
            const status = original?.status || original?.condition || original?.availability || '';
            const details = Array.isArray(result?.details)
              ? result.details.filter(
                (item) =>
                  item?.value !== undefined &&
                  item?.value !== null &&
                  item?.value !== ''
              )
              : [];

            return (
              <MenuItem
                key={result.id || original.id || `${result.title}-${index}`}
                selected={isActive}
                onMouseEnter={() => setActiveResultIndex(index)}
                onClick={() => handleResultClick(result)}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: { xs: 1, sm: 1.5 },
                  width: '100%',
                  minWidth: 0,
                  boxSizing: 'border-box',
                  px: { xs: 1, sm: 1.5 },
                  py: { xs: 1, sm: 1.35 },
                  mb: 0.65,
                  minHeight: { xs: 64, sm: 82, md: 88 },
                  borderRadius: { xs: '11px', sm: '14px' },
                  border: `1px solid ${isActive ? COLORS.primarySoftBorder : COLORS.borderSoft}`,
                  bgcolor: isActive ? COLORS.resultActive : COLORS.surface,
                  boxShadow: isActive
                    ? COLORS.resultActiveShadow
                    : COLORS.subtleShadow,
                  transition: 'all .16s ease',
                  '&:hover': {
                    bgcolor: COLORS.resultActive,
                    borderColor: COLORS.primarySoftBorder,
                    boxShadow: COLORS.resultActiveShadow,
                  },
                  '&.Mui-selected': { bgcolor: COLORS.resultActive },
                  '&.Mui-selected:hover': { bgcolor: COLORS.resultActive },
                }}
              >
                {isActive && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: -1,
                      top: { xs: 10, sm: 14 },
                      bottom: { xs: 10, sm: 14 },
                      width: 3,
                      borderRadius: '0 5px 5px 0',
                      bgcolor: COLORS.primary,
                    }}
                  />
                )}

                <Box
                  sx={{
                    width: { xs: 40, sm: 48 },
                    height: { xs: 40, sm: 48 },
                    minWidth: { xs: 34, sm: 44 },
                    borderRadius: { xs: '11px', sm: '13px' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: isActive ? COLORS.resultIconActive : COLORS.resultIcon,
                    color: COLORS.primary,
                    border: `1px solid ${COLORS.primarySoftBorder}`,
                    mt: 0.1,
                  }}
                >
                  {getSearchResultIcon(resultType)}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 0.5, sm: 0.9 },
                    minWidth: 0,
                    width: '100%',
                  }}>
                    <Typography
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: { xs: 13.5, sm: 15 },
                        fontWeight: 800,
                        color: COLORS.ink,
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {highlightSearchText(
                        result?.title ||
                        original?.name ||
                        original?.challanNumber ||
                        original?.id
                      )}
                    </Typography>

                    <Chip
                      label={resultType}
                      size="small"
                      sx={{
                        flexShrink: 0,
                        display: { xs: 'none', sm: 'inline-flex' },
                        height: 23,
                        borderRadius: '7px',
                        bgcolor: COLORS.hoverSurface,
                        color: COLORS.inkSoft,
                        border: `1px solid ${COLORS.controlBorder}`,
                        fontSize: 9.5,
                        fontWeight: 800,
                        '& .MuiChip-label': { px: 0.9 },
                      }}
                    />
                  </Box>

                  {result?.subtitle && (
                    <Typography
                      sx={{
                        mt: { xs: 0.35, sm: 0.55 },
                        fontSize: { xs: 11.5, sm: 12.5 },
                        color: COLORS.inkSoft,
                        fontWeight: 500,
                        lineHeight: 1.4,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {highlightSearchText(result.subtitle)}
                    </Typography>
                  )}

                  {result?.meta && (
                    <Typography
                      sx={{
                        mt: 0.15,
                        fontSize: { xs: 10.8, sm: 12 },
                        color: COLORS.muted,
                        lineHeight: 1.4,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {highlightSearchText(result.meta)}
                    </Typography>
                  )}

                  {(details.length > 0 || status) && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: { xs: 0.45, sm: 0.7 },
                        mt: { xs: 0.55, sm: 0.8 },
                      }}
                    >
                      {details.slice(0, 3).map((detail, detailIndex) => (
                        <Box
                          key={`${detail.label}-${detailIndex}`}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.35,
                            fontSize: { xs: 9.5, sm: 10.5 },
                            color: COLORS.inkSoft,
                            bgcolor: COLORS.surfaceSoft,
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: '7px',
                            px: { xs: 0.6, sm: 0.8 },
                            py: { xs: 0.25, sm: 0.35 },
                            maxWidth: { xs: '145px', sm: '100%' },
                          }}
                        >
                          <Typography
                            component="span"
                            sx={{ fontSize: 10, color: COLORS.muted, fontWeight: 600 }}
                          >
                            {detail.label}
                          </Typography>
                          <Typography
                            component="span"
                            sx={{
                              fontSize: { xs: 9.5, sm: 10.5 },
                              color: COLORS.inkSoft,
                              fontWeight: 700,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: { xs: 95, sm: 180 },
                            }}
                          >
                            {detail.value}
                          </Typography>
                        </Box>
                      ))}

                      {status && (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.45,
                            fontSize: 10.5,
                            fontWeight: 800,
                            color: COLORS.successText,
                            bgcolor: COLORS.successBg,
                            border: `1px solid ${COLORS.successBorder}`,
                            borderRadius: '999px',
                            px: 0.85,
                            py: 0.35,
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              bgcolor: '#22C55E',
                            }}
                          />
                          {status}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>

                <Box
                  sx={{
                    alignSelf: 'center',
                    width: { xs: 28, sm: 30 },
                    height: { xs: 28, sm: 30 },
                    minWidth: { xs: 28, sm: 30 },
                    borderRadius: { xs: '8px', sm: '9px' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isActive ? COLORS.primary : COLORS.muted,
                    bgcolor: isActive ? COLORS.primarySoft : COLORS.surfaceSoft,
                    border: `1px solid ${isActive ? COLORS.primarySoftBorder : COLORS.border}`,
                    fontSize: 20,
                    lineHeight: 1,
                    transition: 'all .15s ease',
                    transform: isActive ? 'translateX(2px)' : 'none',
                  }}
                >
                  ›
                </Box>
              </MenuItem>
            );
          })}
        </Box>
      )}

      {!searchLoading && searchResults.length > 0 && (
        <Box
          className="header-search-results-footer"
          sx={{
            flex: '0 0 auto',
            minHeight: 0,
            borderTop: `1px solid ${COLORS.border}`,
            bgcolor: COLORS.footer,
            // bgcolor: superfaceSoft,
            px: { xs: 1, sm: 1.5, md: 2 },
            py: { xs: 0.8, sm: 1, md: 1.1 },
          }}
        >
          {/* Result count */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              mb: { xs: 0.75, sm: 0.8, md: 0 },
            }}
          >
            <Typography
              noWrap
              sx={{
                fontSize: { xs: 10.5, sm: 11, md: 11.5 },
                color: COLORS.inkSoft,
                fontWeight: 650,
                lineHeight: 1.3,
              }}
            >
              {searchTotalElements > 0
                ? `Showing ${searchPage * 10 + 1}-${Math.min((searchPage + 1) * 10, searchTotalElements)} of ${searchTotalElements}`
                : `${searchResults.length} result${searchResults.length === 1 ? '' : 's'}`}
            </Typography>

            {searchTotalPages > 1 && (
              <>
                <Box
                  sx={{
                    width: 4,
                    height: 4,
                    mx: 0.8,
                    borderRadius: '50%',
                    bgcolor: COLORS.scrollThumb,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  noWrap
                  sx={{
                    fontSize: { xs: 10.5, sm: 11, md: 11 },
                    color: COLORS.muted,
                    lineHeight: 1.3,
                  }}
                >
                  Page {searchPage + 1} of {searchTotalPages}
                </Typography>
              </>
            )}
          </Box>

          {/* Actions */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: { xs: 0.65, sm: 0.8 },
              width: '100%',
              minWidth: 0,
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
            }}
          >
            {/* Pagination */}
            {searchTotalPages > 1 ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  flexShrink: 0,
                }}
              >
                <IconButton
                  size="small"
                  aria-label="Previous search results page"
                  onClick={() => {
                    if (searchPage > 0) {
                      const newPage = searchPage - 1;
                      setSearchPage(newPage);
                      performSearch(searchQuery, selectedSearchType, newPage);
                    }
                  }}
                  disabled={searchPage === 0}
                  sx={{
                    width: { xs: 32, sm: 30 },
                    height: { xs: 32, sm: 30 },
                    borderRadius: { xs: '8px', sm: '7px' },
                    bgcolor: COLORS.surface,
                    border: `1px solid ${COLORS.controlBorder}`,
                    color: COLORS.inkSoft,
                    flexShrink: 0,
                    '&:hover': { bgcolor: COLORS.hoverSurface, borderColor: COLORS.scrollThumb },
                    '&:disabled': { opacity: 0.4, bgcolor: COLORS.surfaceSoft },
                  }}
                >
                  <ExpandMoreIcon sx={{ transform: 'rotate(90deg)', fontSize: 17 }} />
                </IconButton>

                <IconButton
                  size="small"
                  aria-label="Next search results page"
                  onClick={() => {
                    if (searchPage < searchTotalPages - 1) {
                      const newPage = searchPage + 1;
                      setSearchPage(newPage);
                      performSearch(searchQuery, selectedSearchType, newPage);
                    }
                  }}
                  disabled={searchPage >= searchTotalPages - 1}
                  sx={{
                    width: { xs: 32, sm: 30 },
                    height: { xs: 32, sm: 30 },
                    borderRadius: { xs: '8px', sm: '7px' },
                    bgcolor: COLORS.surface,
                    border: `1px solid ${COLORS.controlBorder}`,
                    color: COLORS.inkSoft,
                    flexShrink: 0,
                    '&:hover': { bgcolor: COLORS.hoverSurface, borderColor: COLORS.scrollThumb },
                    '&:disabled': { opacity: 0.4, bgcolor: COLORS.surfaceSoft },
                  }}
                >
                  <ExpandMoreIcon sx={{ transform: 'rotate(-90deg)', fontSize: 17 }} />
                </IconButton>
              </Box>
            ) : null}

            {/* View all */}
            <Button
              size="small"
              onClick={() => {
                navigate(getSearchListRoute(selectedSearchType), {
                  state: { quickSearch: searchQuery.trim() },
                });
                setShowResultsDropdown(false);
                setResultsAnchor(null);
              }}
              endIcon={<ExpandMoreIcon sx={{ transform: 'rotate(-90deg)', fontSize: 16 }} />}
              sx={{
                minWidth: { xs: 'auto', sm: '110px' },
                flex: { xs: 1, sm: 0 },
                height: { xs: 34, sm: 32 },
                px: { xs: 1, sm: 1.2 },
                borderRadius: { xs: '8px', sm: '7px' },
                textTransform: 'none',
                justifyContent: 'center',
                fontSize: { xs: 10.5, sm: 11.5 },
                fontWeight: 800,
                color: COLORS.primary,
                bgcolor: COLORS.primarySoft,
                whiteSpace: 'nowrap',
                '& .MuiButton-endIcon': {
                  marginLeft: { xs: 0.35, sm: 0.5 },
                  marginRight: 0,
                },
                '&:hover': {
                  bgcolor: COLORS.primarySoftBorder,
                },
              }}
            >
              View all
            </Button>

            {/* Close */}
            <IconButton
              size="small"
              aria-label="Close search results"
              onClick={() => {
                setShowResultsDropdown(false);
                setActiveResultIndex(-1);
                setResultsAnchor(null);
              }}
              sx={{
                width: { xs: 34, sm: 32 },
                height: { xs: 34, sm: 32 },
                borderRadius: { xs: '8px', sm: '7px' },
                bgcolor: COLORS.surface,
                border: `1px solid ${COLORS.controlBorder}`,
                color: COLORS.inkSoft,
                flexShrink: 0,
                '&:hover': {
                  bgcolor: COLORS.dangerBg,
                  borderColor: COLORS.dangerBorder,
                  color: COLORS.dangerHover,
                },
              }}
            >
              <CloseIcon sx={{ fontSize: { xs: 17, sm: 16 } }} />
            </IconButton>
          </Box>
        </Box>
      )}

    </Menu>

  );

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        bgcolor: COLORS.surface,
        color: COLORS.ink,
        borderBottom: `1px solid ${COLORS.border}`,
        boxShadow: 'none',
        height: '64px',
        overflow: 'visible',
        borderRadius: 0,
        zIndex: 300,
        left: isDesktop ? drawerWidth : 0,
        width: isDesktop ? `calc(100% - ${drawerWidth}px)` : '100%',
        transition: 'all .25s ease',
      }}
    >
      <Toolbar sx={{ height: 64, minHeight: '64px !important', px: { xs: 0.75, sm: 2.5 }, display: 'flex', alignItems: 'center', minWidth: 0 }}>
        {showMobileSearch && !isDesktop ? (
          /* Mobile Search View */
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              minWidth: 0,
              gap: { xs: 0.5, sm: 0.75 },
              px: { xs: 0, sm: 0.5 },
            }}
          >
            <IconButton
              aria-label="Close search"
              onClick={() => {
                setShowMobileSearch(false);
                setShowResultsDropdown(false);
                setSearchIconAnchor(null);
                setResultsAnchor(null);
                setSearchQuery('');
                searchRequestIdRef.current += 1;
              }}
              sx={{
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 },
                flexShrink: 0,
                color: COLORS.inkSoft,
                borderRadius: '9px',
              }}
            >
              <ArrowBackIcon sx={{ fontSize: { xs: 20, sm: 22 } }} />
            </IconButton>

            <Box
              onClick={(e) => setSearchIconAnchor(e.currentTarget)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.25,
                height: { xs: 38, sm: 40 },
                px: { xs: 0.8, sm: 1.2 },
                minWidth: { xs: 76, sm: 110 },
                maxWidth: { xs: 104, sm: 140 },
                flexShrink: 1,
                backgroundColor: COLORS.surfaceSoft,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '9px',
                cursor: 'pointer',
                overflow: 'hidden',
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 10.5, sm: 12 },
                  fontWeight: 700,
                  color: COLORS.ink,
                  overflow: 'visible',
                  textOverflow: 'clip',
                  whiteSpace: 'nowrap',
                }}
              >
                {selectedSearchType}
              </Typography>
              <KeyboardArrowDownIcon sx={{ fontSize: 15, color: COLORS.muted, flexShrink: 0 }} />
            </Box>

            <TextField
              fullWidth
              autoFocus
              size="small"
              placeholder={`Search ${selectedSearchType}`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={(e) => setResultsAnchor(e.currentTarget)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setShowResultsDropdown(false);
                  setActiveResultIndex(-1);
                } else if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  if (searchResults.length > 0) {
                    setActiveResultIndex(0);
                  }
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  if (searchResults.length > 0) {
                    setActiveResultIndex(searchResults.length - 1);
                  }
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  if (activeResultIndex >= 0 && searchResults[activeResultIndex]) {
                    handleResultClick(searchResults[activeResultIndex]);
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: COLORS.primary, fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 0,
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  height: { xs: 38, sm: 40 },
                  minWidth: 0,
                  backgroundColor: COLORS.surfaceSoft,
                  borderRadius: '9px',
                  '&:hover': { backgroundColor: COLORS.hoverSurface },
                  //
                  '&.Mui-focused': {
                    backgroundColor: COLORS.surface,
                    boxShadow: `0 0 0 3px ${COLORS.primarySoft}`,
                  },
                },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border },
                '& .MuiInputBase-input': {
                  py: 0.75,
                  px: 0.5,
                  fontSize: { xs: '0.78rem', sm: '0.875rem' },
                  minWidth: 0,
                  color: COLORS.ink,
                  '&::placeholder': {
                    color: COLORS.muted,
                    opacity: 1,
                  },
                },
              }}
            />
          </Box>
        ) : (
          /* Standard View */
          <>
            {/* Mobile Menu Button */}
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={onMobileMenuOpen}
              sx={{ mr: { xs: 1, sm: 2 }, display: { lg: 'none' }, color: COLORS.inkSoft }}
            >
              <MenuIcon sx={{ fontSize: { xs: 24, sm: 26 } }} />
            </IconButton>

            {/* Search Toggle Icon - Mobile only, positioned after menu button */}
            <IconButton
              onClick={() => setShowMobileSearch(true)}
              sx={{
                color: COLORS.inkSoft,
                display: { xs: 'flex', md: 'none' },
                p: 1,
                borderRadius: '10px',
                '&:hover': { backgroundColor: COLORS.hoverSurface },
                mr: { xs: 1, sm: 0 },
              }}
            >
              <SearchIcon sx={{ fontSize: 20 }} />
            </IconButton>

            {/* Left Section: Logo - Only show on mobile where sidebar is hidden */}
            {!isDesktop && (
              <Box
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  width: 'auto',
                  mr: 1,
                  flexShrink: 0,
                  cursor: 'pointer',
                }}
                onClick={() => navigate('/dashboard')}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '7px',
                    backgroundColor: COLORS.primary,
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: '0.8rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1,
                  }}
                >
                  Z
                </Box>
                <Typography sx={{ fontWeight: 700, color: COLORS.ink, fontSize: '0.95rem' }}>
                  Zerlak One
                </Typography>
              </Box>
            )}

            {/* Search */}
            {/* <Box
              sx={{
                width: { xs: '100%', md: 360, lg: 400 },
                maxWidth: { xs: '100%', md: 360, lg: 400 },
                minWidth: 0,
                flexShrink: 1,
                height: 40,
                display: {
                  xs: "none",
                  md: "flex",
                },
                alignItems: "center",
                backgroundColor: COLORS.surfaceSoft,
                border: `1px solid ${COLORS.border}`,
                borderRadius: "10px",
                overflow: "hidden",
                transition: "all .2s ease",
                "&:hover": {
                  borderColor: COLORS.scrollThumb,
                },
                "&:focus-within": {
                  borderColor: COLORS.primary,
                  backgroundColor: COLORS.surface,
                  boxShadow: `0 0 0 3px ${COLORS.primarySoft}`,
                },
              }}
            >
              <Box
                onClick={(e) => setSearchIconAnchor(e.currentTarget)}
                sx={{
                  width: 46,
                  height: '100%',
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  borderRight: `1px solid ${COLORS.border}`,
                  "&:hover": {
                    backgroundColor: COLORS.primarySoft,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <SearchIcon
                    sx={{
                      color: COLORS.primary,
                      fontSize: 17,
                    }}
                  />
                  <KeyboardArrowDownIcon
                    sx={{
                      fontSize: 16,
                      color: COLORS.muted,
                    }}
                  />
                </Box>
              </Box>

              <InputBase
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={(e) => setResultsAnchor(e.currentTarget)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setShowResultsDropdown(false);
                    setActiveResultIndex(-1);
                  } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    if (searchResults.length > 0) {
                      setActiveResultIndex(0);
                    }
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    if (searchResults.length > 0) {
                      setActiveResultIndex(searchResults.length - 1);
                    }
                  } else if (e.key === 'Enter') {
                    e.preventDefault();
                    if (activeResultIndex >= 0 && searchResults[activeResultIndex]) {
                      handleResultClick(searchResults[activeResultIndex]);
                    }
                  }
                }}
                placeholder={`Search in ${selectedSearchType} (/)`}
                sx={{
                  color: COLORS.ink,
                  "& input": {
                    padding: "0 14px",
                    fontSize: 14,
                    fontWeight: 400,
                  },
                  "& input::placeholder": {
                    color: COLORS.muted,
                    opacity: 1,
                  },
                }}
              />
            </Box> */}
            {/* Search */}
            <Box
              sx={{
                width: { xs: '100%', md: 360, lg: 400 },
                maxWidth: { xs: '100%', md: 400, lg: 440 },
                minWidth: 0,
                flexShrink: 1,

                height: 40,

                display: {
                  xs: 'none',
                  md: 'flex',
                },

                alignItems: 'center',

                // IMPORTANT:
                // Keep the entire search bar as ONE surface.
                backgroundColor: COLORS.surfaceSoft,

                border: `1px solid ${COLORS.controlBorder}`,
                borderRadius: '10px',

                overflow: 'hidden',
                boxSizing: 'border-box',

                transition: 'border-color .2s ease, box-shadow .2s ease, background-color .2s ease',

                '&:hover': {
                  borderColor: COLORS.surface,
                },

                '&:focus-within': {
                  backgroundColor: COLORS.surface,
                  borderColor: COLORS.primary,
                  boxShadow: `0 0 0 3px ${COLORS.surfaceSoft}`,
                },
              }}
            >
              {/* Search Type Button */}
              <Box
                onClick={(e) => setSearchIconAnchor(e.currentTarget)}
                sx={{
                  width: 48,
                  height: '100%',
                  flexShrink: 0,

                  cursor: 'pointer',

                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',

                  position: 'relative',

                  // No separate background strip
                  backgroundColor: 'transparent',

                  // Only a subtle divider
                  borderRight: `1px solid ${COLORS.border}`,

                  transition: 'background-color .2s ease',

                  '&:hover': {
                    backgroundColor: COLORS.primarySoft,
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                  }}
                >
                  <SearchIcon
                    sx={{
                      color: COLORS.primary,
                      fontSize: 18,
                    }}
                  />

                  <KeyboardArrowDownIcon
                    sx={{
                      fontSize: 16,
                      color: COLORS.muted,
                    }}
                  />
                </Box>
              </Box>

              {/* Search Input */}
              <InputBase
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={(e) => setResultsAnchor(e.currentTarget)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setShowResultsDropdown(false);
                    setActiveResultIndex(-1);
                  } else if (e.key === 'ArrowDown') {
                    e.preventDefault();

                    if (searchResults.length > 0) {
                      setActiveResultIndex(0);
                    }
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();

                    if (searchResults.length > 0) {
                      setActiveResultIndex(searchResults.length - 1);
                    }
                  } else if (e.key === 'Enter') {
                    e.preventDefault();

                    if (
                      activeResultIndex >= 0 &&
                      searchResults[activeResultIndex]
                    ) {
                      handleResultClick(searchResults[activeResultIndex]);
                    }
                  }
                }}
                placeholder={`Search in ${selectedSearchType} (/)`}
                sx={{
                  flex: 1,
                  minWidth: 0,

                  color: COLORS.ink,

                  backgroundColor: 'transparent',

                  '& input': {
                    height: '38px',
                    boxSizing: 'border-box',

                    padding: '0 14px',

                    fontSize: 14,
                    fontWeight: 400,

                    color: COLORS.ink,

                    backgroundColor: 'transparent',

                    outline: 'none',
                  },

                  '& input::placeholder': {
                    color: COLORS.muted,
                    opacity: 1,
                  },
                }}
              />
            </Box>

            {/* Right Section: Quick Add + Icons */}
            <Box
              sx={{
                ml: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: {
                  xs: 0.5,
                  sm: 0.75
                },
              }}
            >
              {/* Quick Add Button - Desktop/Tablet */}
              <IconButton
                onClick={handleQuickAddMenuOpen}
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  width: 38,
                  height: 38,
                  borderRadius: '10px',
                  backgroundColor: COLORS.primary,
                  color: '#ffffff',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: COLORS.primaryHover,
                    boxShadow: 'none',
                  },
                }}
              >
                <AddIcon sx={{ fontSize: 20 }} />
              </IconButton>

              {/* Quick Add Icon Button - Mobile only */}
              <IconButton
                onClick={handleQuickAddMenuOpen}
                sx={{
                  color: '#fff',
                  backgroundColor: COLORS.primary,
                  p: 1,
                  display: { xs: 'flex', sm: 'none' },
                  borderRadius: '10px',
                  '&:hover': {
                    backgroundColor: COLORS.primaryHover,
                  },
                }}
              >
                <AddIcon sx={{ fontSize: 20 }} />
              </IconButton>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1, display: { xs: 'none', md: 'block' }, borderColor: COLORS.border }} />

              {/* WhatsApp Credits */}
              {loadingWhatsappCredits ? (
                <Skeleton
                  variant="rectangular"
                  width={80}
                  height={38}
                  sx={{ borderRadius: '10px' }}
                />
              ) : (
                <Chip
                  icon={<WhatsappIcon style={{ color: '#25D366' }} />}
                  label={whatsappCredits !== undefined && whatsappCredits !== null ? whatsappCredits : '0'}
                  sx={{
                    height: { xs: 32, sm: 38 },
                    borderRadius: '10px',
                    backgroundColor: COLORS.hoverSurface,
                    color: COLORS.inkSoft,
                    fontWeight: 600,
                    fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                    '& .MuiChip-icon': {
                      color: '#25D366',
                      fontSize: { xs: 16, sm: 18 },
                    },
                  }}
                />
              )}
              {/* Notifications */}
              <IconButton onClick={handleNotificationMenuOpen} sx={{ color: COLORS.inkSoft, p: { xs: 0.5, sm: 1.25 }, width: { xs: '32px', sm: '38px' }, height: { xs: '32px', sm: '38px' }, borderRadius: '10px', '&:hover': { backgroundColor: COLORS.hoverSurface } }}>
                <Badge
                  badgeContent={4}
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: 10,
                      minWidth: 16,
                      height: 16,
                      right: 2,
                      top: 2,
                      backgroundColor: COLORS.danger,
                      color: '#fff',
                    }
                  }}
                >
                  <NotificationsIcon sx={{ fontSize: 19 }} />
                </Badge>
              </IconButton>

              {/* Profile */}
              <Box
                onClick={handleProfileMenuOpen}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 0.5,
                  ml: 0.25,
                  borderRadius: '10px',
                  cursor: 'pointer',
                  backgroundColor: COLORS.primarySoft,
                  border: `1px solid ${COLORS.primarySoftBorder}`,
                  '&:hover': {
                    backgroundColor: COLORS.primarySoftBorder,
                  },
                }}
              >
                <Box
                  onClick={(event) => {
                    event.stopPropagation();
                    if (window.innerWidth <= 768) {
                      handleProfileMenuOpen(event);
                      return;
                    }
                    handleLogoClick(event);
                  }}
                  sx={{
                    width: { xs: 36, sm: 40, md: 42 },
                    height: { xs: 36, sm: 40, md: 42 },
                    minWidth: { xs: 36, sm: 40, md: 42 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    flexShrink: 0,
                    borderRadius: '10px',
                    backgroundColor: COLORS.primarySoft,
                    border: `1px solid ${COLORS.primarySoftBorder}`,
                    position: 'relative',
                    cursor: 'pointer',
                    '& img': {
                      width: '100%',
                      height: '100%',
                      display: 'block',
                      objectFit: 'contain',
                      objectPosition: 'center',
                      maxWidth: '100%',
                      maxHeight: '100%',
                    },
                  }}
                >
                  {logoData ? (
                    <img
                      src={logoData}
                      alt="Business Logo"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <AccountCircleIcon
                      sx={{
                        fontSize: { xs: 20, sm: 22, md: 24 },
                        color: COLORS.primary,
                      }}
                    />
                  )}
                </Box>
                {loadingBusinessName ? (
                  <Skeleton
                    variant="text"
                    width={120}
                    height={20}
                    sx={{ display: { xs: 'none', md: 'block' } }}
                  />
                ) : (
                  <Typography
                    sx={{
                      fontSize: { xs: '0.8rem', sm: '0.85rem' },
                      fontWeight: 600,
                      color: COLORS.ink,
                      display: { xs: 'none', md: 'block' },
                    }}
                  >
                    {businessName || 'Business Name'}
                  </Typography>
                )}
                <ExpandMoreIcon
                  sx={{
                    fontSize: 18,
                    color: COLORS.muted,
                    display: { xs: 'none', md: 'block' },
                    flexShrink: 0,
                  }}
                />
              </Box>
            </Box>
          </>
        )}
      </Toolbar>

      {renderSearchTypeMenu()}
      {renderSearchResultsMenu()}

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onMouseLeave={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        // PaperProps={{
        //   sx: {
        //     minWidth: 220,
        //     mt: 1,
        //     borderRadius: '10px',
        //     border: `1px solid ${COLORS.border}`,
        //     backgroundColor: COLORS.surface,
        //     color: COLORS.ink,
        //     boxShadow: COLORS.menuShadow,
        //   },
        // }}
        PaperProps={{
          sx: {
            minWidth: 350,
            maxHeight: 400,
            mt: 1,
            borderRadius: '10px',
            border: `1px solid ${COLORS.border}`,
            backgroundColor: COLORS.surface,
            color: COLORS.ink,
            boxShadow: COLORS.menuShadow,
            overflow: 'hidden',

            '& .notification-title': {
              color: COLORS.ink,
            },

            '& .notification-text': {
              color: COLORS.inkSoft,
            },

            '& .notification-time': {
              color: COLORS.muted,
            },
          },
        }}
      >
        <MenuItem onClick={() => { navigate('/dashboard/profile'); handleMenuClose(); }} sx={{ gap: 2 }}>
          <AccountCircleIcon sx={{ fontSize: 18, color: COLORS.primary }} />
          My Profile
        </MenuItem>
        <MenuItem onClick={() => { navigate('/dashboard/settings'); handleMenuClose(); }} sx={{ gap: 2 }}>
          <SettingsIcon sx={{ fontSize: 18, color: COLORS.primary }} />
          Settings
        </MenuItem>
        <MenuItem onClick={() => { navigate('/dashboard/users?tab=users'); handleMenuClose(); }} sx={{ gap: 2 }}>
          <GroupIcon sx={{ fontSize: 18, color: COLORS.primary }} />
          Users
        </MenuItem>
        <MenuItem onClick={toggleColorMode} sx={{ gap: 2 }}>
          {mode === 'dark' ? <LightModeIcon sx={{ fontSize: 18, color: COLORS.primary }} /> : <DarkModeIcon sx={{ fontSize: 18, color: COLORS.primary }} />}
          {mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ color: COLORS.danger, gap: 2 }}>
          <LogoutIcon sx={{ fontSize: 18, color: COLORS.danger }} />
          Logout
        </MenuItem>
      </Menu>

      {/* Business Logo Popup */}
      <Dialog
        open={logoModalOpen}
        onClose={() => {
          setLogoModalOpen(false);
          setLogoEditMode(false);
          setLogoDeleteConfirm(false);
          setLogoPreview(null);
          setSelectedLogoFile(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            overflow: 'hidden',
            backgroundColor: COLORS.surface,
            color: COLORS.ink,
            border: `1px solid ${COLORS.border}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: '1.1rem',
              fontWeight: 700,
              color: COLORS.ink,
            }}
          >
            {logoEditMode ? 'Edit Logo' : 'Business Logo'}
          </Typography>
          <IconButton
            onClick={() => {
              setLogoModalOpen(false);
              setLogoEditMode(false);
              setLogoDeleteConfirm(false);
              setLogoPreview(null);
              setSelectedLogoFile(null);
            }}
            sx={{
              color: COLORS.muted,
              '&:hover': {
                backgroundColor: COLORS.hoverSurface,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            pt: 1,
            pb: 2,
          }}
        >
          {logoDeleteConfirm ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: '1rem',
                  color: COLORS.ink,
                  mb: 2,
                }}
              >
                Are you sure you want to delete this logo? This will restore the default avatar.
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <Button
                  onClick={() => setLogoDeleteConfirm(false)}
                  sx={{
                    textTransform: 'none',
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleDeleteLogo}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '9px',
                    backgroundColor: COLORS.danger,
                    '&:hover': {
                      backgroundColor: COLORS.dangerHover,
                    },
                  }}
                >
                  Delete
                </Button>
              </Box>
            </Box>
          ) : logoEditMode ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '12px',
                  border: `2px dashed ${COLORS.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  backgroundColor: COLORS.surfaceSoft,
                }}
              >
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt="Logo Preview"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <PhotoCameraIcon
                    sx={{
                      fontSize: 40,
                      color: COLORS.muted,
                    }}
                  />
                )}
              </Box>

              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                onChange={handleLogoFileChange}
                style={{
                  display: 'none',
                }}
                id="logo-file-input"
              />
              <Button
                component="label"
                htmlFor="logo-file-input"
                variant="outlined"
                sx={{
                  textTransform: 'none',
                  borderRadius: '9px',
                }}
              >
                Choose Image
              </Button>

              {selectedLogoFile && (
                <Typography
                  sx={{
                    fontSize: 13,
                    color: COLORS.inkSoft,
                  }}
                >
                  {selectedLogoFile.name}
                </Typography>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 150,
                  height: 150,
                  borderRadius: '12px',
                  border: `1px solid ${COLORS.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  backgroundColor: COLORS.surfaceSoft,
                }}
              >
                {logoData ? (
                  <img
                    src={logoData}
                    alt="Business Logo"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <AccountCircleIcon
                    sx={{
                      fontSize: 80,
                      color: COLORS.muted,
                    }}
                  />
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}
              >
                <Button
                  onClick={() => setLogoEditMode(true)}
                  startIcon={<EditIcon />}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '9px',
                  }}
                >
                  Edit
                </Button>
                <Button
                  onClick={handleDownloadLogo}
                  startIcon={<DownloadIcon />}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '9px',
                  }}
                >
                  Download
                </Button>
                {logoData && (
                  <Button
                    onClick={() => setLogoDeleteConfirm(true)}
                    startIcon={<DeleteIcon />}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '9px',
                      color: COLORS.danger,
                    }}
                  >
                    Delete
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>

        {logoEditMode && (
          <DialogActions
            sx={{
              px: 3,
              pb: 3,
              gap: 1,
            }}
          >
            <Button
              onClick={() => {
                setLogoEditMode(false);
                setLogoPreview(null);
                setSelectedLogoFile(null);
              }}
              sx={{
                textTransform: 'none',
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveLogo}
              disabled={!logoPreview}
              sx={{
                textTransform: 'none',
                borderRadius: '9px',
              }}
            >
              Save Logo
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Notification Menu */}
      <Menu
        anchorEl={notificationAnchor}
        open={Boolean(notificationAnchor)}
        onClose={handleMenuClose}
        onMouseLeave={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            minWidth: 350,
            maxHeight: 400,
            mt: 1,
            borderRadius: '10px',
            border: `1px solid ${COLORS.border}`,
            backgroundColor: COLORS.surface,
            color: COLORS.ink,
            boxShadow: COLORS.menuShadow,
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: `1px solid ${COLORS.border}` }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700, color: COLORS.ink }}>
            Notifications
          </Typography>
        </Box>
        {notificationItems.map((notification, index) => (
          <MenuItem
            key={index}
            onClick={handleMenuClose}
            sx={{ p: 2, borderBottom: index < notificationItems.length - 1 ? `1px solid ${COLORS.border}` : 'none' }}
          >
            <Box sx={{ width: '100%' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: COLORS.ink }}>
                {notification.title}
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.inkSoft, fontSize: '0.8125rem' }}>
                {notification.description}
              </Typography>
              <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.75rem', mt: 0.5 }}>
                {notification.time}
              </Typography>
            </Box>
          </MenuItem>
        ))}
        <Box sx={{ p: 1.5, textAlign: 'center' }}>
          <Button
            onClick={handleMenuClose}
            sx={{ color: COLORS.primary, textTransform: 'none', fontWeight: 600 }}
          >
            View All Notifications
          </Button>
        </Box>
      </Menu>

      {/* Quick Add Menu - Mega Dropdown */}
      <Menu
        anchorEl={quickAddAnchor}
        open={Boolean(quickAddAnchor)}
        onClose={handleMenuClose}
        onMouseLeave={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '90vw', md: '800px', lg: '1000px' },
            maxWidth: '1000px',
            mt: 0.5,
            backgroundColor: COLORS.surface,
            borderRadius: '14px',
            boxShadow: isDark ? '0 20px 40px -8px rgba(0, 0, 0, 0.55)' : '0 20px 40px -8px rgba(15, 23, 42, 0.18)',
            border: `1px solid ${COLORS.border}`,
            overflow: 'hidden',
          },
        }}
        slotProps={{
          paper: {
            sx: {
              left: '50% !important',
              transform: 'translateX(-50%) !important',
            }
          }
        }}
      >
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(3, 1fr)'
          },
          gap: { xs: '16px', sm: '24px', md: '28px', lg: '32px' },
          p: { xs: '16px', sm: '20px', md: '24px', lg: '28px' },
          justifyContent: 'center',
          alignItems: 'flex-start'
        }}>
          {quickAddCategories.map((category) => (
            <Box key={category.title} sx={{
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Typography
                sx={{
                  fontSize: { xs: '11px', sm: '12px', md: '12px', lg: '12px' },
                  fontWeight: 700,
                  color: COLORS.muted,
                  mb: { xs: '10px', sm: '12px', md: '14px', lg: '16px' },
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {category.title}
              </Typography>
              {category.items.map((item) => (
                <Box
                  key={item.label}
                  onClick={() => { navigate(item.path); handleMenuClose(); }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: '8px', sm: '9px', md: '9px', lg: '10px' },
                    padding: { xs: '6px 8px', sm: '7px 8px', md: '7px 8px', lg: '8px 8px' },
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: { xs: '12px', sm: '13px', md: '13px', lg: '14px' },
                    color: COLORS.ink,
                    ml: '-8px',
                    '&:hover': {
                      backgroundColor: COLORS.primarySoft,
                    },
                  }}
                >
                  <Box sx={{ color: COLORS.primary, fontSize: { xs: '14px', sm: '15px', md: '15px', lg: '16px' }, display: 'flex' }}>
                    {item.icon}
                  </Box>
                  <Typography sx={{ fontSize: { xs: '12px', sm: '13px', md: '13px', lg: '14px' } }}>{item.label}</Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Menu>

      {/* Logout Confirmation Modal */}
      <ConfirmModal
        isOpen={logoutModal.isOpen}
        onConfirm={confirmLogout}
        onCancel={cancelLogout}
        title="Logout"
        message="Are you sure you want to logout? You will need to login again to access your account."
        confirmText="Logout"
        cancelText="Cancel"
        type="danger"
      />
    </AppBar>
  );
};

export default Header;
